/** @jsx jsx */
import React, { memo, useMemo, type ReactNode } from 'react';
import { css, jsx } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { token } from '@atlaskit/tokens';
import { Tokens } from '@atlassian/jira-custom-theme-constants/src/constants.tsx';
import { TeamButtonComponent } from '@atlassian/teams';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import type { NavigationTeamButton_project$key } from '@atlassian/jira-relay/src/__generated__/NavigationTeamButton_project.graphql';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id/src/index.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';

type TeamButtonProps = {
	scope: 'project' | 'board';
	projectFragment: NavigationTeamButton_project$key | null;
};

export const NavigationTeamButton = memo(({ scope, projectFragment }: TeamButtonProps) => {
	const project = useFragment(
		graphql`
			fragment NavigationTeamButton_project on JiraProject {
				id @required(action: THROW)
			}
		`,
		projectFragment,
	);

	const attributes = useMemo(() => ({ scope }), [scope]);
	const { data: orgId } = useOrgId();
	const isAnonymous = useIsAnonymous();

	if (isAnonymous || !project) {
		return null;
	}

	return (
		<JSErrorBoundary
			id="jira-horizontal-nav-header-team-button"
			packageName="jira-horizontal-nav"
			fallback="unmount"
			teamName="Jira Cosmos"
		>
			<ContextualAnalyticsData sourceName="horizontal-nav-header" attributes={attributes}>
				<TeamButtonComponent
					numberOfTeams={1}
					containerId={project.id}
					orgId={orgId}
					Wrapper={CustomWrapper}
				/>
			</ContextualAnalyticsData>
		</JSErrorBoundary>
	);
});

const CustomWrapper = ({ children }: { children: ReactNode }) => (
	<div css={wrapperStyle}>{children}</div>
);

const wrapperStyle = css({
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	'--ds-border': Tokens.COLOR_BORDER,
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	'--ds-text-subtle': Tokens.COLOR_TEXT_SUBTLE,
	marginRight: token('space.075'),
});
